import { useEffect, useMemo, useState } from 'react'

import { RangeOperationDialog } from '@/components/edit/range-operation-dialog'
import { Button } from '@/components/ui/button'
import { useCurrentActivity } from '@/hooks/use-current-activity'
import { extractGpsPoints } from '@/lib/geo'
import { selectSelectedActivity, useActivityStore } from '@/store/activity-store'

export interface SetGpsConstantDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Sets every record's GPS position in the chosen range to one fixed
 * coordinate -- entered manually, or picked by clicking the map. Picking
 * on the map closes this dialog (so the map underneath is actually
 * clickable, see `ActivityMap`'s `mapPickMode`) and reopens it once a
 * point is picked or the pick is cancelled, with whatever coordinate was
 * picked (if any) filled in.
 */
export function SetGpsConstantDialog({ open, onOpenChange }: SetGpsConstantDialogProps) {
  const loaded = useActivityStore(selectSelectedActivity)
  const mapPickMode = useActivityStore((s) => s.mapPickMode)
  const pickedMapPoint = useActivityStore((s) => s.pickedMapPoint)
  const setMapPickMode = useActivityStore((s) => s.setMapPickMode)
  const setPickedMapPoint = useActivityStore((s) => s.setPickedMapPoint)

  const [lat, setLat] = useState('')
  const [lon, setLon] = useState('')
  const [pendingPick, setPendingPick] = useState(false)

  // Only fetches while actually open -- see `useCurrentActivity`'s doc
  // comment for why (this dialog, like every edit dialog, stays mounted
  // while closed). While loading, `hasGps` stays `false` -- a harmless,
  // momentary "enter coordinates manually" fallback rather than blocking
  // the rest of the dialog on this one check.
  const { result } = useCurrentActivity(open ? loaded?.history : undefined)
  const hasGps = useMemo(
    () => result != null && extractGpsPoints(result.activity.records).length > 0,
    [result],
  )

  // Reopens once the map pick this dialog started finishes, whether by
  // actually picking a point or by the user cancelling from the map's
  // banner -- both just end `mapPickMode`, so this one effect covers both.
  // Deliberately kept as an effect (not moved to render-time state
  // adjustment, unlike the prefill logic in `RangeOperationDialog`/
  // `CropDialog`): this reacts to an *external* store's state
  // transitioning and then calls external side effects of its own
  // (`setPickedMapPoint`, the parent's `onOpenChange`) -- exactly the
  // "synchronize with an external system" case effects are for, not the
  // "derive local state from a prop" case the lint rule below is
  // actually about. The lint rule's own message suggests an
  // event-callback API as the "proper" alternative (the map calling an
  // `onPickComplete` prop instead of this dialog polling a shared
  // boolean), which would be a reasonable bigger refactor but isn't
  // justified for this one interaction in this milestone.
  useEffect(() => {
    if (!pendingPick || mapPickMode) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see comment above the effect
    setPendingPick(false)
    if (pickedMapPoint != null) {
      setLat(pickedMapPoint.lat.toFixed(6))
      setLon(pickedMapPoint.lon.toFixed(6))
      setPickedMapPoint(null)
    }
    onOpenChange(true)
  }, [pendingPick, mapPickMode, pickedMapPoint, onOpenChange, setPickedMapPoint])

  const latNum = Number(lat)
  const lonNum = Number(lon)
  const coordsValid =
    lat.trim() !== '' &&
    lon.trim() !== '' &&
    Number.isFinite(latNum) &&
    Number.isFinite(lonNum) &&
    latNum >= -90 &&
    latNum <= 90 &&
    lonNum >= -180 &&
    lonNum <= 180

  return (
    <RangeOperationDialog
      open={open}
      onOpenChange={onOpenChange}
      idPrefix="set-gps-constant"
      title="Set GPS constant"
      description="Sets every record's GPS position in this range to one fixed coordinate -- useful for a stretch where you stayed in one place (e.g. an aid station) but the device recorded drift."
      confirmLabel="Set GPS constant"
      buildOperation={(start, end) => ({
        type: 'setGpsConstant',
        start,
        end,
        lat: latNum,
        lon: lonNum,
      })}
      extraValid={coordsValid}
      extraFields={
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <label htmlFor="set-gps-constant-lat" className="text-foreground text-xs font-medium">
                Latitude
              </label>
              <input
                id="set-gps-constant-lat"
                type="number"
                step="any"
                min={-90}
                max={90}
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                placeholder="e.g. 37.9715"
                className="border-input bg-background text-foreground focus-visible:ring-ring rounded-md border px-2 py-1 text-sm outline-none focus-visible:ring-2"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="set-gps-constant-lon" className="text-foreground text-xs font-medium">
                Longitude
              </label>
              <input
                id="set-gps-constant-lon"
                type="number"
                step="any"
                min={-180}
                max={180}
                value={lon}
                onChange={(e) => setLon(e.target.value)}
                placeholder="e.g. 23.7267"
                className="border-input bg-background text-foreground focus-visible:ring-ring rounded-md border px-2 py-1 text-sm outline-none focus-visible:ring-2"
              />
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasGps}
            className="w-fit"
            onClick={() => {
              setPendingPick(true)
              setMapPickMode(true)
              onOpenChange(false)
            }}
          >
            Pick on map
          </Button>
          {!hasGps && (
            <p className="text-muted-foreground text-xs">
              This activity has no GPS data to pick a point on -- enter coordinates manually.
            </p>
          )}
        </div>
      }
    />
  )
}
